import SnapLineManager from '@framework/editor/src/page/index/components/canvas/snapline-manager';
import { IMoveElementManager } from '@src/slide/application/service/move-element-manager/move-element-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import TextAdjustManager from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import _get from 'lodash/get';
import type { IMultiRect } from '@src/slide/application/service/edit-action-services/edit-process/edit-process.interface';
import EditProcess from '@src/slide/application/service/edit-action-services/edit-process';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import type ShapeModel from '@src/slide/domain/ui-model/element-modeles/shape-model';
import type { IResizeElementHandler, ResizeEventMap } from './resize-element-handler.interface';
import type { BorderSketchManager } from '@src/slide/application/service/border-sketch-manager';
import { IGuidesManager } from '@src/slide/application/service/guides-manager/index.interface';
import { browser } from '@src/utils/agent';
import ResizeLogicPC from '@src/framework/editor/src/page/index/components/canvas/resize-logic-pc';
import ElementInfoAdapter from '../element-info';
import SelectedElementWrapperUtil from '@src/framework/editor/src/page/index/components/canvas/edit-panel/selected-element-layer/utils';
import isLockAspect from '@src/framework/editor/src/js/utils/lock-aspect';
import { isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IAbsorbManager } from '@src/slide/application/service/absorb-manager/absorb-manager.interface';
import { SubscribableService } from '@src/slide/application/service/subscribable-service';
import { ResizeEventType } from './resize-element-handler.interface';
import { ILinkManager } from '@src/slide/application/service/link-manager/core/link-manager.interface';
import { ISnapLineService } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';

export default class ResizeElementHandler extends SubscribableService<ResizeEventMap> implements IResizeElementHandler {
    /**
     * 当前缩放翻转的元素model
     */
    private elementModel: IElementBaseModel;

    /**
     * 多选的rect
     */
    private multiRect: IMultiRect | null;

    /**
     * 通用编辑之后的逻辑
     */
    private editProcessLogic: EditProcess;

    /**
     * 是否在组合内，是组合子元素
     */
    private isGroupChildren: boolean;
    private isSmartArtChildren: boolean;
    private guidesManager?: IGuidesManager;
    private absorbManager?: IAbsorbManager;
    private snapLineService?: ISnapLineService;
    private resizeLogicPCInstance?: ResizeLogicPC;

    public constructor(
        private editingStateManager: EditingStateManager,
        private moveElementManager: IMoveElementManager,
        private selectionService: ISelectionService,
        private borderSketchManager: BorderSketchManager,
        private linkManager: ILinkManager,
    ) {
        super();
    }

    public setGuidesManagerDelegate(guidesManager: IGuidesManager): void {
        this.guidesManager = guidesManager;
    }

    public setAbsorbManagerDelegate(absorbManager?: IAbsorbManager): void {
        this.absorbManager = absorbManager;
    }

    public setSnapLineServiceDelegate(snapLineService?: ISnapLineService): void {
        this.snapLineService = snapLineService;
    }

    /**
     * resize开始处理逻辑
     */
    public handleResizeStart = (elementModel: IElementBaseModel, e, scale: number, mode): void => {
        this.emit(ResizeEventType.RESIZE_START, { elementModel, e: e?.event ?? e });

        this.elementModel = elementModel;
        this.isGroupChildren = _get(elementModel, 'parent.type') === UIModelType.GROUP;
        this.isSmartArtChildren = _get(elementModel, 'parent.type') === UIModelType.SMART_ART;
        this.editProcessLogic = new EditProcess(this.editingStateManager, elementModel, this.borderSketchManager);
        this.editProcessLogic.setSelectionService(this.selectionService);
        this.editProcessLogic.setGuidesManagerDelegate(this.guidesManager);
        this.editProcessLogic.setAbsorbManagerDelegate(this.absorbManager);
        this.editProcessLogic.setSnapLineServiceDelegate(this.snapLineService);
        this.multiRect = SnapLineManager.getInstance().getMultiRect();
        SnapLineManager.getInstance().calculateSnapLines();
        this.initResizeLogicPC(elementModel, e, scale, mode);

        if (this.isGroupChildren) {
            this.handleResizeStartForRootGroup();
            return;
        }
        // if (this.isSmartArtChildren) {
        //     this.handleResizeStartForRootSmartArt();
        //     return;
        // }

        this.selectionService.selectedElements.clear(SelectedEleMgrType.GROUP);

        this.linkManager.linkFloatBarService.hide(); // 关闭链接浮层
        this.moveElementManager.checkIsDelayUpdatePosing();
        this.editingStateManager.updateElementResizeState(true);
        const action = this.getActionOfReport();
        this.reportForAction(action);
    };

    /**
     * resize过程中的逻辑
     */
    public handleResize = (e, originalSize, nextSize, mode, sizeMap?): void => {
        this.emit(ResizeEventType.RESIZING, {
            elementModel: this.elementModel,
            e,
            mode,
            nextSize,
            originalSize,
            multiRect: this.multiRect,
            sizeMap,
        });

        if (TextAdjustManager.getInstance().enableAdjustText()) {
            TextAdjustManager.getInstance().updateElementWrapType(this.elementModel as ShapeModel, mode);
        }
        const offsetFrame = this.editProcessLogic
            ?.runConstraints(e, originalSize, nextSize, mode, this.multiRect, sizeMap);
        if (this.isGroupChildren) {
            this.handleResizeForRootGroup(e, originalSize, nextSize, mode, null, offsetFrame, sizeMap);
        // } else if (this.isSmartArtChildren) {
        //     this.handleResizeForRootSmartArt(e, originalSize, nextSize, mode, null, offsetFrame, sizeMap);
        } else {
            this.editingStateManager.updateElementResizeState(true, mode);
        }
    };

    /**
     * resize结束的逻辑
     * @param mode
     */
    public handleResizeStop = (mode: string): void => {
        this.emit(ResizeEventType.RESIZE_STOP, { elementModel: this.elementModel });

        this.multiRect = null;
        SnapLineManager.getInstance().handleDragResizeStop();
        this.resizeLogicPCInstance?.stopResize();
        if (this.isGroupChildren) {
            this.handleResizeStopForRootGroup(mode);
            return;
        }
        // if (this.isSmartArtChildren) {
        //     this.handleResizeStopForRootSmartArt(mode);
        //     return;
        // }
        this.editProcessLogic?.persistSize(mode);
        this.editingStateManager.updateElementResizeState(false);
        this.editProcessLogic?.updateOperationFrame(null);
    };

    /**
     * 组合内元素resize开始逻辑
     */
    public handleResizeStartForRootGroup = (): void => {
        this.moveElementManager.checkIsDelayUpdatePosing();
        this.editingStateManager.updateElementResizeState(true);
        let action;
        if (this.selectionService.selectedElements
            .selectedArrayForType(SelectedEleMgrType.GROUP).length === 1) { // 单选元素
            const [model] = this.selectionService
                .selectedElements
                .selectedArrayForType(SelectedEleMgrType.GROUP);
            switch (model.type) {
                case UIModelType.SHAPE:
                    action = TDWActionType.EDIT_SIZE;
                    break;
                case UIModelType.IMAGE:
                    action = TDWActionType.IMG_SIZE;
                    break;
            }
        }
        // if (this.selectionService.selectedElements
        //     .selectedArrayForType(SelectedEleMgrType.SMART_ART).length === 1) { // 单选元素
        //     const [model] = this.selectionService
        //         .selectedElements
        //         .selectedArrayForType(SelectedEleMgrType.SMART_ART);
        //     switch (model.type) {
        //         case UIModelType.SHAPE:
        //             action = TDWActionType.EDIT_SIZE;
        //             break;
        //         case UIModelType.IMAGE:
        //             action = TDWActionType.IMG_SIZE;
        //             break;
        //     }
        // }
        this.reportForAction(action);
    };

    /**
     * 组合内元素resize过程中逻辑
     */
    public handleResizeForRootGroup = (e, originalSize, nextSize, mode, srcRect, offsetFrame, sizeMap?): void => {
        this.editProcessLogic?.runConstraintsForGroup(e, originalSize, nextSize, mode, null, offsetFrame, sizeMap);
        this.editingStateManager.updateElementResizeState(true, mode);
    };

    /**
     * smartArt内元素resize过程中逻辑
     */
    public handleResizeForRootSmartArt = (e, originalSize, nextSize, mode, srcRect, offsetFrame, sizeMap?): void => {
        this.editProcessLogic?.runConstraintsForSmartArt(e, originalSize, nextSize, mode, null, offsetFrame, sizeMap);
        this.editingStateManager.updateElementResizeState(true, mode);
    };

    /**
     * 组合内元素resize结束逻辑
     */
    public handleResizeStopForRootGroup = (mode: string): void => {
        this.editingStateManager.updateElementResizeState(false, mode);
        if (this.elementModel.rootGroupModel) {
            this.elementModel.rootGroupModel.childResizeFinished = true;
        }
        this.editProcessLogic?.persistSizeForGroup(this.elementModel.rootGroupModel);
        // 此时将mode置空
        this.editingStateManager.updateElementResizeState(false);
        this.editProcessLogic?.updateOperationFrameForGroup(null);
    };

    /**
     * smartArt内元素resize结束逻辑
     */
    public handleResizeStopForRootSmartArt = (mode: string): void => {
        this.editingStateManager.updateElementResizeState(false, mode);
        if (this.elementModel.rootSmartArtModel) {
            this.elementModel.rootSmartArtModel.childResizeFinished = true;
        }
        this.editProcessLogic?.persistSizeForSmartArt(this.elementModel.rootSmartArtModel);
        // 此时将mode置空
        this.editingStateManager.updateElementResizeState(false);
        this.editProcessLogic?.updateOperationFrameForSmartArt(null);
    };
    private initResizeLogicPC(elementModel, e, scale: number, mode: string) {
        if (browser.isPc) {
            const relativeInfo = SelectedElementWrapperUtil.relativeInfoForInner(elementModel);
            const frameInfo = SelectedElementWrapperUtil.frameInfoForInner(elementModel);
            this.resizeLogicPCInstance = new ResizeLogicPC({
                startSize: ElementInfoAdapter.sizeInfo(elementModel),
                startFlip: ElementInfoAdapter.flipInfo(elementModel),
                scale,
                ...relativeInfo,
                isIframe: isIframeShapeModel(elementModel),
                rotate: frameInfo.rotate,
                onResize: this.handleResize,
                modelIsLockAspect: Number(isLockAspect(this.elementModel.type, this.elementModel.props)),
                resizeMode: mode,
            });
            this.resizeLogicPCInstance.startResize(e);
        }
    }

    /**
     * 上报所需要的action字段
     */
    private getActionOfReport() {
        let action;
        const { selectedElements } = this.selectionService;
        if (selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE).length === 1) { // 单选元素
            const [model] = selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
            switch (model.type) {
                case UIModelType.SHAPE:
                    action = TDWActionType.EDIT_SIZE;
                    break;
                case UIModelType.IMAGE:
                    action = TDWActionType.IMG_SIZE;
                    break;
            }
        }
        return action;
    }

    /**
     * tdw上报action
     * @param action
     */
    private reportForAction(action) {
        if (action) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_OBJ,
                action,
            });
        }
    }
}
